
import React, { useReducer, useEffect, useRef } from 'react';
import {
    Box, Button, FormControl, InputLabel, Select, MenuItem, Typography, Alert, TextField, Autocomplete, CircularProgress,
    IconButton, Popper, ClickAwayListener, Paper, List, ListItemButton, ListItemText, Divider, ToggleButtonGroup, ToggleButton,
    LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api';
import { getSession } from '../../auth';
import useNotification from '../Notification';
import { lockedReducer } from '../../utils/defaultReducer';
import OpportunityMarkers from './opportunity-markers';
import NewCustomFieldForm from './NewCustomFieldForm';

const filterOptions = [
    { value: 'contact_group', label: 'Que fazem parte do(s) segmento(s)' },
    { value: 'contact_enterprise', label: 'Que fazem parte da(s) empresa(s)' },
    { value: 'email', label: 'Que possuam e-mail com final' },
    { value: 'telephone', label: 'Que possuam telefone com DDD' },
    { value: 'created_gte', label: 'Com data de criação maior, ou igual a' },
    { value: 'created_lte', label: 'Com data de criação menor, ou igual a' },
    { value: 'birthday', label: 'Aniversariantes do mês' },
];

const companyFilterOptions = [
    { value: 'contact_group', label: 'Que fazem parte do(s) segmento(s)' },
    { value: 'name', label: 'Nome da empresa contém' },
    { value: 'email', label: 'Que possuam e-mail com final' },
    { value: 'telephone', label: 'Que possuam telefone com DDD' },
    { value: 'created_gte', label: 'Com data de criação maior, ou igual a' },
    { value: 'created_lte', label: 'Com data de criação menor, ou igual a' },
    { value: 'social_reason', label: 'Razão social contém' },
    { value: 'activity_area', label: 'Área de atuação' },
    { value: 'custom_field', label: 'Campo customizado' },
];

const months = {
    1: "Janeiro",
    2: "Fevereiro",
    3: "Março",
    4: "Abril",
    5: "Maio",
    6: "Junho",
    7: "Julho",
    8: "Agosto",
    9: "Setembro",
    10: "Outubro",
    11: "Novembro",
    12: "Dezembro"
};

const colorOptions = [
    { label: 'Azul', value: '#3498db' },
    { label: 'Vermelho', value: '#e74c3c' },
    { label: 'Verde', value: '#2ecc71' },
    { label: 'Laranja', value: '#f39c12' },
    { label: 'Roxo', value: '#9b59b6' },
    { label: 'Rosa', value: '#e91e63' },
    { label: 'Ciano', value: '#00bcd4' },
    { label: 'Cinza', value: '#95a5a6' },
];

const formatActivityCode = (value = '') => {
    const digits = String(value).replace(/\D/g, '').slice(0, 7);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}-${digits.slice(4, 5)}-${digits.slice(5)}`;
};

const initialState = {
    step: 1,
    importType: 'contact',
    contactFilter: '',
    companyFilter: '',
    selectedSegments: [],
    selectedCompanySegments: [],
    selectedEnterprises: [],
    filterValue: '',
    companyFilterValue: '',
    segments: [],
    enterprises: [],
    loadingSegments: false,
    loadingEnterprises: false,
    inputValue: '',
    enterpriseInputValue: '',
    opportunityName: '',
    selectedColumn: '',
    columns: [],
    tags: [],
    allTags: [],
    customFields: [],
    companyCustomFields: [],
    selectedCompanyCustomField: '',
    companyCustomFieldValue: '',
    loadingCompanyCustomFields: false,
    selectedCustomFields: {},
    tagsOpen: false,
    customFieldsOpen: false,
    showNewTagForm: false,
    showNewCustomFieldForm: false,
    newTag: { name: '', color: '#3498db' },
    isCreatingOpportunities: false,
};

const ImportLeadsModal = ({ onClose, selectedJourney, firstColumn, setBoard, setEdit, onOpportunitiesCreated }) => {
    const [state, dispatch] = useReducer(lockedReducer, {
        ...initialState,
        selectedColumn: firstColumn?.id || '',
        columns: selectedJourney?.columns || [],
    });
    const tagsRef = React.useRef(null);
    const customFieldsRef = React.useRef(null);
    const customFieldTypeSelectOpenRef = useRef(false);
    const isSubmittingRef = useRef(false);

    const activeFilter = state.importType === 'contact' ? state.contactFilter : state.companyFilter;
    const activeFilterValue = state.importType === 'contact' ? state.filterValue : state.companyFilterValue;
    const activeSegments = state.importType === 'contact' ? state.selectedSegments : state.selectedCompanySegments;
    const selectedCompanyCustomField = state.companyCustomFields.find(field => String(field.id) === String(state.selectedCompanyCustomField));

    useEffect(() => {
        dispatch({
            contactFilter: '',
            companyFilter: '',
            selectedSegments: [],
            selectedCompanySegments: [],
            selectedEnterprises: [],
            filterValue: '',
            companyFilterValue: '',
            selectedCompanyCustomField: '',
            companyCustomFieldValue: '',
            inputValue: '',
            enterpriseInputValue: ''
        });
    }, [state.importType]);

    useEffect(() => {
        dispatch({ selectedSegments: [], filterValue: '', inputValue: '' });
    }, [state.contactFilter]);

    useEffect(() => {
        dispatch({ selectedCompanySegments: [], selectedEnterprises: [], companyFilterValue: '', selectedCompanyCustomField: '', companyCustomFieldValue: '', inputValue: '', enterpriseInputValue: '' });
    }, [state.companyFilter]);

    useEffect(() => {
        if (selectedJourney?.id) {
            dispatch({ columns: selectedJourney.columns || [] });
            fetchTags();
            fetchCustomFields();
            fetchCompanyCustomFields();
        }
    }, [selectedJourney?.id]);

    useEffect(() => {
        if (state.step === 2 && selectedJourney?.id) {
            const reloadColumns = async () => {
                try {
                    const response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/journeys/${selectedJourney.id}/`);
                    if (response.status === 200) {
                        dispatch({ columns: response.data.columns || [] });
                    }
                } catch (error) {
                }
            };
            reloadColumns();
        }
    }, [state.step]);

    const fetchTags = async () => {
        try {
            const response = await api.get(`/api/enterprises/${getSession().profile.enterprise.id}/journeys/${selectedJourney.id}/tags/`);
            dispatch({ allTags: response.data.results || response.data });
        } catch (error) {
        }
    };

    const fetchCustomFields = async () => {
        try {
            const response = await api.get(`/api/enterprises/${getSession().profile.enterprise.id}/journeys/${selectedJourney.id}/custom_fields/`);
            dispatch({ customFields: response.data.results || response.data });
        } catch (error) {
        }
    };

    const fetchCompanyCustomFields = async () => {
        try {
            dispatch({ loadingCompanyCustomFields: true });
            const response = await api.get(`/api/enterprises/${getSession().profile.enterprise.id}/enterprise_custom_fields/`);
            dispatch({ companyCustomFields: response.data.results || response.data, loadingCompanyCustomFields: false });
        } catch (error) {
            dispatch({ loadingCompanyCustomFields: false });
        }
    };

    const handleSaveCustomField = async (fieldData) => {
        try {
            const response = await api.post(
                `/api/enterprises/${getSession().profile.enterprise.id}/journeys/${selectedJourney.id}/custom_fields/`,
                fieldData
            );
            if (response.status === 201) {
                dispatch({
                    customFields: [...state.customFields, response.data],
                    showNewCustomFieldForm: false
                });
                useNotification('Sucesso', 'Campo personalizado criado!', 'success');
            }
        } catch (error) {
            useNotification('Erro', 'Erro ao criar campo personalizado', 'danger');
        }
    };

    useEffect(() => {
        if (activeFilter === 'contact_group') {
            dispatch({ loadingSegments: true });
            const enterpriseId = getSession().profile.enterprise.id;
            const url = `/api/enterprises/${enterpriseId}/contact_group/get_group_filtered/?limit=10&offset=0&name__icontains=${state.inputValue}`;
            api.get(url)
                .then(res => {
                    const segmentosArray = Array.isArray(res.data) ? res.data : (res.data.results || []);
                    dispatch({ segments: segmentosArray.map(each => ({ id: each.id, label: each.name })), loadingSegments: false });
                })
                .catch(() => dispatch({ loadingSegments: false }));
        } else {
            dispatch({ segments: [] });
        }
    }, [activeFilter, state.inputValue]);

    useEffect(() => {
        if (activeFilter === 'contact_group') {
            dispatch({ inputValue: '' });
        }
    }, [activeFilter]);

    useEffect(() => {
        if (state.contactFilter === 'contact_enterprise') {
            dispatch({ loadingEnterprises: true });
            const enterpriseId = getSession().profile.enterprise.id;
            const url = `/api/enterprises/${enterpriseId}/contact_enterprises/?search=${state.enterpriseInputValue}`;
            api.get(url)
                .then(res => {
                    const empresasArray = Array.isArray(res.data) ? res.data : (res.data.results || []);
                    dispatch({ enterprises: empresasArray.map(each => ({ id: each.id, label: each.name })), loadingEnterprises: false });
                })
                .catch(() => dispatch({ loadingEnterprises: false }));
        } else {
            dispatch({ enterprises: [] });
        }
    }, [state.contactFilter, state.companyFilter, state.enterpriseInputValue]);

    const handleSendLeads = async () => {
        // Guard against double submission (React Strict Mode)
        if (isSubmittingRef.current) {
            return;
        }

        if (!state.opportunityName.trim()) {
            useNotification('Atenção', 'Digite um nome para a oportunidade', 'warning');
            return;
        }

        if (!state.selectedColumn) {
            useNotification('Atenção', 'Selecione uma coluna', 'warning');
            return;
        }

        if (!activeFilter) {
            useNotification('Atenção', `Selecione um filtro para buscar ${state.importType === 'contact' ? 'contatos' : 'empresas'}`, 'warning');
            return;
        }

        if (activeFilter === 'contact_group' && activeSegments.length === 0) {
            useNotification('Atenção', 'Selecione pelo menos um segmento', 'warning');
            return;
        }

        if (state.contactFilter === 'contact_enterprise' && state.selectedEnterprises.length === 0) {
            useNotification('Atenção', 'Selecione pelo menos uma empresa', 'warning');
            return;
        }

        if (state.importType === 'company' && state.companyFilter === 'custom_field' && (!state.selectedCompanyCustomField || state.companyCustomFieldValue === '')) {
            useNotification('Atenção', 'Selecione um campo customizado e informe o valor', 'warning');
            return;
        }

        isSubmittingRef.current = true;
        dispatch({ isCreatingOpportunities: true });
        const enterpriseId = getSession().profile.enterprise.id;
        
        const payload = {
            import_type: state.importType,
            contact_filter: state.contactFilter,
            company_filter: state.companyFilter,
            selected_segments: state.selectedSegments.map(s => s.id),
            selected_company_segments: state.selectedCompanySegments.map(s => s.id),
            selected_enterprises: state.selectedEnterprises.map(e => e.id),
            filter_value: state.filterValue,
            company_filter_value: state.companyFilterValue,
            company_custom_field_id: state.selectedCompanyCustomField,
            company_custom_field_value: state.companyCustomFieldValue,
            column_id: state.selectedColumn,
            opportunity_name: state.opportunityName,
            tags: state.tags,
            custom_fields: state.selectedCustomFields
        };

        try {
            const response = await api.post(
                `enterprises/${enterpriseId}/journeys/${selectedJourney.id}/opportunities/create_mass_opportunity`,
                payload,
                { timeout: 5 * 60 * 1000 } // importações grandes podem passar do timeout padrão da API
            );

            // Se a importação demorar mais que o timeout, o interceptor da API resolve
            // a chamada com o próprio erro em vez de rejeitá-la, então `response` não
            // terá `data`. Isso não significa que a importação falhou: o backend segue
            // processando em segundo plano e o board se atualiza sozinho via websocket.
            if (!response || typeof response.data !== 'object' || response.data === null) {
                useNotification(
                    'Importação em andamento',
                    'A importação está demorando mais que o normal, mas continua sendo processada em segundo plano. As oportunidades aparecerão na coluna automaticamente assim que forem criadas.',
                    'info',
                    12000
                );
                onClose();
                return;
            }

            if (response.data.created_count > 0) {
                const ignoredCount = response.data.ignored_count || response.data.ignored || 0;
                const fallbackMessage = ignoredCount > 0
                    ? `${response.data.created_count} oportunidade(s) criada(s). ${ignoredCount} registro(s) ignorado(s) por já possuir oportunidade com o mesmo nome.`
                    : `${response.data.created_count} oportunidade(s) criada(s) com sucesso`;
                useNotification(
                    'Sucesso!',
                    response.data.message || fallbackMessage,
                    'success'
                );

                // Chama callback passando as oportunidades criadas
                if (onOpportunitiesCreated) {
                    onOpportunitiesCreated(state.selectedColumn, response.data.opportunities);
                }

                onClose();
            } else if (response.data.error) {
                useNotification('Erro', response.data.error, 'danger');
            } else {
                useNotification('Atenção', response.data.message || 'Nenhuma oportunidade foi criada', 'warning');
            }
        } catch (error) {
            useNotification('Erro', error.response?.data?.error || 'Erro ao importar oportunidades', 'danger');
        } finally {
            dispatch({ isCreatingOpportunities: false });
            isSubmittingRef.current = false;
        }
    };

    const handleValidateAndGoToStep2 = () => {
        if (!activeFilter) {
            useNotification('Atenção', `Selecione um filtro para buscar ${state.importType === 'contact' ? 'contatos' : 'empresas'}`, 'warning');
            return;
        }

        if (activeFilter === 'contact_group' && activeSegments.length === 0) {
            useNotification('Atenção', 'Selecione pelo menos um segmento', 'warning');
            return;
        }

        if (state.contactFilter === 'contact_enterprise' && state.selectedEnterprises.length === 0) {
            useNotification('Atenção', 'Selecione pelo menos uma empresa', 'warning');
            return;
        }

        if (state.importType === 'company' && state.companyFilter === 'custom_field' && (!state.selectedCompanyCustomField || state.companyCustomFieldValue === '')) {
            useNotification('Atenção', 'Selecione um campo customizado e informe o valor', 'warning');
            return;
        }

        dispatch({ step: 2 });
    };

    return state.step === 1 ? (
        <Box p={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Importar oportunidades</Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Selecione se a importação será feita por contatos ou empresas e escolha os filtros para criar oportunidades.
            </Alert>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Importar por</Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={state.importType}
                    onChange={(_, value) => {
                        if (value) dispatch({ importType: value });
                    }}
                >
                    <ToggleButton value="contact">Contato</ToggleButton>
                    <ToggleButton value="company">Empresa</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Filtro</InputLabel>
                <Select
                    label="Filtro"
                    value={activeFilter}
                    onChange={e => dispatch(state.importType === 'contact' ? { contactFilter: e.target.value } : { companyFilter: e.target.value })}
                >
                    {(state.importType === 'contact' ? filterOptions : companyFilterOptions).map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {activeFilter === 'contact_group' && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Escolha um ou mais segmentos abaixo</Typography>
                    <Autocomplete
                        multiple
                        options={state.segments}
                        getOptionLabel={option => option.label}
                        value={activeSegments}
                        onChange={(_, value, reason) => {
                            dispatch(state.importType === 'contact' ? { selectedSegments: value } : { selectedCompanySegments: value });
                            if (reason === 'selectOption' || reason === 'removeOption' || reason === 'createOption' || reason === 'clear') {
                                dispatch({ inputValue: '' });
                            }
                        }}
                        inputValue={state.inputValue}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') dispatch({ inputValue: value });
                        }}
                        loading={state.loadingSegments}
                        filterOptions={(options) => options}
                        noOptionsText={state.loadingSegments ? "Carregando..." : "Nenhuma opção disponível"}
                        renderInput={params => (
                            <TextField {...params} label="Segmentos" size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {state.loadingSegments ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            )}
            {state.contactFilter === 'contact_enterprise' && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Escolha uma ou mais empresas abaixo</Typography>
                    <Autocomplete
                        multiple
                        options={state.enterprises}
                        getOptionLabel={option => option.label}
                        value={state.selectedEnterprises}
                        onChange={(_, value) => dispatch({ selectedEnterprises: value })}
                        inputValue={state.enterpriseInputValue}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input') dispatch({ enterpriseInputValue: value });
                        }}
                        loading={state.loadingEnterprises}
                        filterOptions={(options) => options}
                        noOptionsText={state.loadingEnterprises ? "Carregando..." : "Nenhuma opção disponível"}
                        renderInput={params => (
                            <TextField {...params} label="Empresas" size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {state.loadingEnterprises ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            )}
            {activeFilter === 'name' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Digite parte do nome da empresa
                    </Typography>
                    <TextField label="Nome da empresa" size="small" fullWidth sx={{ mb: 2 }} value={state.companyFilterValue} onChange={e => dispatch({ companyFilterValue: e.target.value })} />
                </Box>
            )}
            {activeFilter === 'social_reason' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Digite parte da razão social
                    </Typography>
                    <TextField label="Razão social" size="small" fullWidth sx={{ mb: 2 }} value={state.companyFilterValue} onChange={e => dispatch({ companyFilterValue: e.target.value })} />
                </Box>
            )}
            {activeFilter === 'email' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        {state.importType === 'company'
                            ? 'Digite o domínio abaixo (ex: instant.com.br)'
                            : 'Digite o final do e-mail abaixo (ex: instant.com.br)'}
                    </Typography>
                    <TextField
                        label={state.importType === 'company' ? 'Domínio' : 'Final do email'}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={activeFilterValue}
                        onChange={e => dispatch(state.importType === 'contact' ? { filterValue: e.target.value } : { companyFilterValue: e.target.value })}
                    />
                </Box>
            )}
            {state.importType === 'company' && activeFilter === 'activity_area' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Digite o código da área de atuação
                    </Typography>
                    <TextField
                        label="Código"
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={state.companyFilterValue}
                        onChange={e => dispatch({ companyFilterValue: formatActivityCode(e.target.value) })}
                    />
                </Box>
            )}
            {state.importType === 'company' && activeFilter === 'custom_field' && (
                <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Campo customizado</InputLabel>
                        <Select
                            label="Campo customizado"
                            value={state.selectedCompanyCustomField}
                            onChange={e => dispatch({ selectedCompanyCustomField: e.target.value, companyCustomFieldValue: '' })}
                            disabled={state.loadingCompanyCustomFields}
                        >
                            {state.companyCustomFields.map(field => (
                                <MenuItem key={field.id} value={field.id}>{field.field_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedCompanyCustomField?.field_type === 'boolean' && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Valor</InputLabel>
                            <Select
                                label="Valor"
                                value={state.companyCustomFieldValue}
                                onChange={e => dispatch({ companyCustomFieldValue: e.target.value })}
                            >
                                <MenuItem value="true">Sim</MenuItem>
                                <MenuItem value="false">Não</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    {selectedCompanyCustomField?.field_type === 'select' && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Valor</InputLabel>
                            <Select
                                label="Valor"
                                value={state.companyCustomFieldValue}
                                onChange={e => dispatch({ companyCustomFieldValue: e.target.value })}
                            >
                                {selectedCompanyCustomField.options?.map(option => (
                                    <MenuItem key={option.id || option.value} value={option.value}>{option.label || option.value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {selectedCompanyCustomField && !['boolean', 'select'].includes(selectedCompanyCustomField.field_type) && (
                        <TextField
                            label="Valor"
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                            type={selectedCompanyCustomField.field_type === 'integer' ? 'number' : selectedCompanyCustomField.field_type === 'date' ? 'date' : 'text'}
                            multiline={selectedCompanyCustomField.field_type === 'textarea'}
                            rows={selectedCompanyCustomField.field_type === 'textarea' ? 3 : 1}
                            value={state.companyCustomFieldValue}
                            onChange={e => dispatch({ companyCustomFieldValue: e.target.value })}
                            InputLabelProps={selectedCompanyCustomField.field_type === 'date' ? { shrink: true } : undefined}
                        />
                    )}
                    {state.loadingCompanyCustomFields && <CircularProgress size={20} />}
                    {!state.loadingCompanyCustomFields && state.companyCustomFields.length === 0 && (
                        <Alert severity="info">Nenhum campo customizado de empresa encontrado.</Alert>
                    )}
                </Box>
            )}
            {activeFilter === 'telephone' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Digite o DDD abaixo (ex: 21, para Rio de Janeiro)
                    </Typography>
                    <TextField
                        label="DDD"
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={activeFilterValue}
                        onChange={e => dispatch(state.importType === 'contact' ? { filterValue: e.target.value } : { companyFilterValue: e.target.value })}
                    />
                </Box>
            )}
            {activeFilter === 'created_gte' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Escolha a data abaixo
                    </Typography>
                    <TextField label="Data" type="date" size="small" fullWidth sx={{ mb: 2 }} value={activeFilterValue} onChange={e => dispatch(state.importType === 'contact' ? { filterValue: e.target.value } : { companyFilterValue: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Box>
            )}
            {activeFilter === 'created_lte' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Escolha a data abaixo
                    </Typography>
                    <TextField label="Data" type="date" size="small" fullWidth sx={{ mb: 2 }} value={activeFilterValue} onChange={e => dispatch(state.importType === 'contact' ? { filterValue: e.target.value } : { companyFilterValue: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Box>
            )}
            {state.importType === 'contact' && state.contactFilter === 'birthday' && (
                <Box sx={{ mb: 2 }}>
                    <Typography className="typography-title" variant="h6" gutterBottom>
                        Escolha o mês abaixo
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Mês</InputLabel>
                        <Select label="Mês" onChange={(event) => dispatch({ filterValue: event.target.value })} value={state.filterValue}>
                            {Object.entries(months).map(([key, value]) => (
                                <MenuItem value={key}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleValidateAndGoToStep2}
                    disabled={
                        !activeFilter ||
                        (activeFilter === 'contact_group' && activeSegments.length === 0) ||
                        (state.contactFilter === 'contact_enterprise' && state.selectedEnterprises.length === 0) ||
                        ((['email', 'telephone', 'created_gte', 'created_lte'].includes(activeFilter) || (state.importType === 'contact' && activeFilter === 'birthday') || (state.importType === 'company' && ['name', 'social_reason', 'activity_area'].includes(activeFilter))) && !activeFilterValue) ||
                        (state.importType === 'company' && activeFilter === 'custom_field' && (!state.selectedCompanyCustomField || state.companyCustomFieldValue === ''))
                    }
                >
                    Próximo
                </Button>
            </Box>
        </Box>
    ) : (
        <Box p={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" onClick={() => dispatch({ step: 1 })}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">Configurar Oportunidades</Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <TextField
                fullWidth
                size="small"
                label="Nome da oportunidade"
                placeholder="Ex: Proposta, Reunião, Contato..."
                value={state.opportunityName}
                onChange={(e) => dispatch({ opportunityName: e.target.value })}
                sx={{ mb: 3 }}
                helperText={`O título da oportunidade será: {Nome da oportunidade} - {Nome ${state.importType === 'contact' ? 'do contato' : 'da empresa'}}`}
            />

            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Coluna para colocar as oportunidades</InputLabel>
                <Select 
                    label="Coluna para colocar as oportunidades" 
                    value={state.selectedColumn} 
                    onChange={e => dispatch({ selectedColumn: e.target.value })}
                >
                    {state.columns?.map(col => (
                        <MenuItem key={col.id} value={col.id}>{col.title}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                        Marcadores (opcional)
                    </Typography>
                    <IconButton
                        ref={tagsRef}
                        onClick={() => dispatch({ tagsOpen: !state.tagsOpen })}
                        size="small"
                    >
                        <AddIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {state.tags.map((tagId) => {
                        const tag = state.allTags.find((t) => t.id === tagId);
                        if (!tag) return null;
                        return (
                            <Box
                                key={tag.id}
                                sx={{
                                    padding: ".15rem .25rem",
                                    borderRadius: ".25rem",
                                    fontSize: ".8em",
                                    backgroundColor: tag.color,
                                    cursor: 'pointer',
                                    color: '#fff'
                                }}
                                onClick={() => dispatch({ tags: state.tags.filter(id => id !== tagId) })}
                            >
                                {tag.name}
                            </Box>
                        );
                    })}
                </Box>
                <OpportunityMarkers 
                    tagsOpen={state.tagsOpen} 
                    setTagsOpen={(data)=>dispatch({tagsOpen: data})} 
                    allTags={state.allTags} 
                    newTagsRef={tagsRef}
                    setParent={(changeParent) => {
                        dispatch(changeParent(state))
                    }} 
                    parent={state} 
                    selectedJourney={selectedJourney}
                />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                        Campos Personalizados (opcional)
                    </Typography>
                    <IconButton
                        ref={customFieldsRef}
                        onClick={() => dispatch({ customFieldsOpen: !state.customFieldsOpen })}
                        size="small"
                    >
                        <AddIcon />
                    </IconButton>
                </Box>

                {state.customFields.map((field) => (
                    <TextField
                        key={field.id}
                        size="small"
                        label={field.name}
                        fullWidth
                        sx={{ mt: 1, mb: 1 }}
                        type={field.field_type === 'number' ? 'number' : 'text'}
                        multiline={field.field_type === 'textarea'}
                        rows={field.field_type === 'textarea' ? 3 : 1}
                        value={state.selectedCustomFields[field.id] || ''}
                        onChange={(e) => dispatch({
                            selectedCustomFields: {
                                ...state.selectedCustomFields,
                                [field.id]: e.target.value
                            }
                        })}
                        required={field.required}
                    />
                ))}

                <Popper
                    open={state.customFieldsOpen}
                    anchorEl={customFieldsRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 9999 }}
                >
                    <ClickAwayListener onClickAway={(event) => {
                        if (
                            customFieldTypeSelectOpenRef.current ||
                            event.target?.closest?.(".paper-select-dropdown")
                        ) {
                            return;
                        }

                        dispatch({ customFieldsOpen: false });
                    }}>
                        <Paper sx={{ p: 2, minWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                            {!state.showNewCustomFieldForm && (
                                <Box>
                                    {state.customFields.length === 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            Jornada sem campos personalizados.
                                        </Box>
                                    )}
                                    {state.customFields.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                                Campos existentes:
                                            </Typography>
                                            {state.customFields.map((field) => (
                                                <Box key={field.id} sx={{ mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                        • {field.name} ({field.field_type === "text" ? "Texto" : field.field_type === "textarea" ? "Texto longo" : field.field_type === "number" ? "Número" : field.field_type === "select" ? "Lista" : field.field_type === "date" ? "Data" : field.field_type === "checkbox" ? "Checkbox" : field.field_type}){field.required && " - Obrigatório"}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Button
                                        fullWidth
                                        onClick={() => dispatch({ showNewCustomFieldForm: true })}
                                        size="small"
                                    >
                                        <AddIcon sx={{ mr: 0.5 }} />
                                        Criar campo
                                    </Button>
                                </Box>
                            )}
                            {state.showNewCustomFieldForm && (
                                <NewCustomFieldForm
                                    onBack={() => dispatch({ showNewCustomFieldForm: false })}
                                    onSave={handleSaveCustomField}
                                    onSelectOpenChange={(isOpen) => {
                                        customFieldTypeSelectOpenRef.current = isOpen;
                                    }}
                                />
                            )}
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            </Box>

            {state.isCreatingOpportunities && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Criando oportunidades... Isso pode levar alguns instantes, conforme o tamanho da lista.
                    Você pode aguardar ou fechar esta janela, as oportunidades aparecerão na coluna
                    automaticamente assim que forem criadas.
                    <LinearProgress sx={{ mt: 1 }} />
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch({ step: 1 })}
                    disabled={state.isCreatingOpportunities}
                >
                    Voltar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendLeads}
                    disabled={state.isCreatingOpportunities}
                >
                    {state.isCreatingOpportunities ? 'Enviando...' : 'ENVIAR'}
                </Button>
            </Box>
        </Box>
    );
};

export default ImportLeadsModal;